// src/pages/schooladmin/Attendance.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Badge, Spinner
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, doc, setDoc, onSnapshot, query, where,
  serverTimestamp, getDocs
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmtTime = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD MMM, hh:mm A") : "—");
const clean = (s = "") => String(s).trim().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
const norm = (v = "") => String(v).toLowerCase().replace(/\s+/g, " ").trim();
const makeAttId = (date, className, section, studentId) =>
  `${dayjs(date).format("YYYYMMDD")}_${clean(className)}_${clean(section)}_${studentId}`;

// stable per-student key regardless of source collection
const studentKeyOf = (s = {}) =>
  String(s.id ?? s.uid ?? s.userId ?? s.auth?.uid ?? "");

// roles
const normalizeRole = (r = "") => {
  const v = String(r).toLowerCase().trim();
  if (v === "admin") return "superadmin";
  if (v === "school_admin" || v === "school") return "schooladmin";
  return v; // superadmin | schooladmin | teacher | student
};

const STATUSES = [
  { key: "present", label: "Present", code: "P", variant: "success" },
  { key: "absent", label: "Absent", code: "A", variant: "danger" },
  { key: "late", label: "Late", code: "L", variant: "warning" },
  { key: "excused", label: "Excused / Leave", code: "E", variant: "info" },
  { key: "halfday", label: "Half-day", code: "H", variant: "secondary" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));

// Built-in options
const CLASS_FALLBACK = [
  ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),
  "Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts",
  "Foundation 6","Foundation 7","Foundation 8","Foundation 9","Foundation 10",
  "NEET","JEE","Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE",
  "LT","NEET Repeaters","JEE Repeaters","Crash Course","Integrated",
  "B.Tech","BE","B.Sc","B.Com","BA","BBA","BCA",
  "MBBS","BDS","Pharmacy","Nursing",
  "Diploma / Polytechnic","ITI",
  "Other Higher Education",
];

const SECTION_FALLBACK = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

const BRANCH_FALLBACK = [
  "Science","Commerce","Arts",
  "PCM","PCB","PCMB",
  "CSE","IT","ECE","EEE","Mechanical","Civil",
  "AI/ML","Data Science",
  "Biotechnology","Chemical","Mechatronics","Automobile",
  "General","Finance","Marketing","HR","Business Analytics","Logistics","Computer Applications",
];

// normalize common aliases (e.g., "leave" => "excused")
const normalizeStatusInput = (k = "") => {
  const v = String(k || "").toLowerCase().trim();
  if (v === "leave" || v === "leaves") return "excused";
  return v;
};

/* ---------------- component ---------------- */
export default function AttendancePage() {
  const { school, loading: loadingSchool } = useSchool();

  // auth & role
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);

  // filters / mode
  const [mode, setMode] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [branch, setBranch] = useState("");
  const [qText, setQText] = useState("");

  // referenced data
  const [classesData, setClassesData] = useState([]); // hydrate later if needed
  const [students, setStudents] = useState([]);       // includes .key (stable id)

  // attendance data
  const [attDaily, setAttDaily] = useState(new Map()); // Map keyed by studentId and studentKey
  const [attMonthly, setAttMonthly] = useState([]);    // raw docs for the month

  // quick lookup for a student's daily record (by id, then stable key)
  const recOf = React.useCallback(
    (student) => {
      if (!student) return null;
      return attDaily.get(student.id) || attDaily.get(studentKeyOf(student)) || null;
    },
    [attDaily]
  );

  // UI states
  const [loading, setLoading] = useState(true);
  const [overlay, setOverlay] = useState(false);

  // selection & drafts
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [draftStatus, setDraftStatus] = useState(new Map()); // studentId -> draft status

  // saving states
  const [savingRowIds, setSavingRowIds] = useState(new Set());
  const [savingBulk, setSavingBulk] = useState(false);

  const searchRef = useRef(null);
  const masterChkRef = useRef(null);

  /* ---- auth & role ---- */
  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (u?.uid) {
        const unsubUser = onSnapshot(doc(db, "users", u.uid), (snap) => {
          setRole((snap.data() || {}).role || null);
        });
        return () => unsubUser();
      } else {
        setRole(null);
      }
    });
    return () => off();
  }, []);

  /* ---- class/section options ---- */
  const classOptions = useMemo(() => {
    const fromFs = classesData.map(c => (c.className || "").trim()).filter(Boolean);
    const uniq = Array.from(new Set(fromFs)).sort();
    return uniq.length ? uniq : CLASS_FALLBACK;
  }, [classesData]);

  const sectionOptions = useMemo(() => {
    if (!cls) return [];
    const wanted = norm(cls);
    const secs = classesData
      .filter(c => norm(c.className) === wanted)
      .map(c => (c.section || "").toString().trim())
      .filter(Boolean)
      .map(s => s.toUpperCase());
    const uniq = Array.from(new Set(secs)).sort();
    return uniq.length ? uniq : SECTION_FALLBACK;
  }, [classesData, cls]);

  // branch options for current class/section
  const branchOptions = useMemo(() => {
    const fromStudents = students
      .filter(s => (!cls || norm(s.className) === norm(cls)) && (!sec || norm(s.section) === norm(sec)))
      .map(s => (s.branch || s.stream || "").toString().trim())
      .filter(Boolean)
      .map(b => b.toUpperCase());
    const uniq = Array.from(new Set(fromStudents)).sort();
    return uniq.length ? uniq : BRANCH_FALLBACK;
  }, [students, cls, sec]);

  useEffect(() => { if (!branch && branchOptions.length === 1) setBranch(branchOptions[0]); }, [branchOptions]); // eslint-disable-line
  useEffect(() => { if (!cls && classOptions.length) setCls(classOptions[0]); }, [classOptions]); // eslint-disable-line
  useEffect(() => { if (!sec && sectionOptions.length) setSec(sectionOptions[0]); }, [sectionOptions]); // eslint-disable-line

  // clear selection/drafts when context changes
  useEffect(() => { setSelectedIds(new Set()); setDraftStatus(new Map()); }, [cls, sec, selectedDate, mode]);

  // clear visible data while snapshots load
  useEffect(() => {
    setStudents([]);
    setAttDaily(new Map());
    if (mode === "monthly") setAttMonthly([]);
  }, [cls, sec, branch, selectedDate, selectedMonth, mode]);

  /* ---- load students (school subcollection first → fallback /users) ---- */
  useEffect(() => {
    if (!school?.id || !cls) return;
    setLoading(true);
    setOverlay(true);
    let isStale = false;

    const wantedClass = norm(cls);
    const wantedSec = norm(sec);

    const colA = collection(db, "schools", school.id, "students");
    const qA = query(colA, where("className", "==", cls));

    let unsubB = null;

    const unsubA = onSnapshot(
      qA,
      (snap) => {
        if (isStale) return;
        const listA = snap.docs
          .map((d) => {
            const s = d.data() || {};
            return {
              id: d.id,
              key: studentKeyOf({ id: d.id, ...s }),
              name: s.name || s.displayName || "—",
              rollNo: s.rollNo || s.studentId || "",
              photoURL: s.photoURL || "",
              className: s.className || "",
              section: s.section || "",
              branch: s.branch || s.stream || "",
              status: s.status || "active",
              lastAttendanceStatus: s.lastAttendanceStatus || "",
              lastAttendanceDate: toDate(s.lastAttendanceDate),
            };
          })
          .filter((s) => norm(s.className) === wantedClass)
          .filter((s) => !sec || norm(s.section) === wantedSec)
          .filter((s) => !branch || norm(s.branch) === norm(branch))
          .filter((s) => norm(s.status) !== "inactive")
          .sort((a, b) => a.name.localeCompare(b.name));

        if (listA.length) {
          setStudents(listA);
          setLoading(false);
          setOverlay(false);
          if (unsubB) { unsubB(); unsubB = null; }
          return;
        }

        // Fallback to /users (role=student)
        const colB = collection(db, "users");
        const qB = query(
          colB,
          where("schoolId", "==", school.id),
          where("role", "==", "student"),
          where("className", "==", cls)
        );

        unsubB = onSnapshot(
          qB,
          (snap2) => {
            if (isStale) return;
            const listB = snap2.docs
              .map((d) => {
                const s = d.data() || {};
                return {
                  id: d.id,
                  key: studentKeyOf({ id: d.id, ...s }),
                  name: s.name || s.displayName || "—",
                  rollNo: s.rollNo || s.studentId || "",
                  photoURL: s.photoURL || "",
                  className: s.className || "",
                  section: s.section || "",
                  branch: s.branch || s.stream || "",
                  status: s.status || "active",
                  lastAttendanceStatus: "",
                  lastAttendanceDate: null,
                };
              })
              .filter((s) => norm(s.className) === wantedClass)
              .filter((s) => !sec || norm(s.section) === wantedSec)
              .filter((s) => !branch || norm(s.branch) === norm(branch))
              .filter((s) => norm(s.status) !== "inactive")
              .sort((a, b) => a.name.localeCompare(b.name));

            setStudents(listB);
            setLoading(false);
            setOverlay(false);
          },
          () => { if (!isStale) { setLoading(false); setOverlay(false); } }
        );
      },
      () => { if (!isStale) { setLoading(false); setOverlay(false); } }
    );

    return () => {
      isStale = true;
      try { unsubA(); } catch {}
      try { if (unsubB) unsubB(); } catch {}
    };
  }, [school?.id, cls, sec, branch]);

  /* ---- attendance (daily) ---- */
  useEffect(() => {
    if (!school?.id || !cls || !sec || mode !== "daily") return;
    let isStale = false;

    const start = dayjs(selectedDate).startOf("day").toDate();
    const end   = dayjs(selectedDate).endOf("day").toDate();

    const col  = collection(db, "schools", school.id, "attendance");
    const qRef = query(
      col,
      where("className", "==", cls),
      where("section", "==", sec),
      where("date", ">=", start),
      where("date", "<=", end)
    );

    const unsub = onSnapshot(qRef, (snap) => {
      if (isStale) return;

      // latest per studentId/studentKey
      const latest = new Map();

      snap.forEach((d) => {
        const x = d.data() || {};
        const rec = {
          id: d.id,
          studentId: x.studentId || "",
          studentKey: x.studentKey || x.studentId || "",
          status: normalizeStatusInput(x.status || ""),
          note: x.note || "",
          updatedAt: toDate(x.updatedAt) || toDate(x.createdAt) || toDate(x.date),
          markedByName: x.markedByName || "",
          _ts:
            +toDate(x.updatedAt) ||
            +toDate(x.createdAt) ||
            +toDate(x.date) ||
            0,
        };

        const setIfNewer = (k) => {
          if (!k) return;
          const prev = latest.get(k);
          if (!prev || rec._ts > prev._ts) latest.set(k, rec);
        };

        setIfNewer(rec.studentId);
        setIfNewer(rec.studentKey);
      });

      setAttDaily(latest);
    });

    return () => { isStale = true; try { unsub(); } catch {} };
  }, [school?.id, cls, sec, selectedDate, mode]);

  /* ---- CSV Export helpers ---- */
  const filteredStudents = useMemo(() => {
    const q = qText.trim().toLowerCase();
    if (!q) return students;
    return students.filter(s => `${s.name} ${s.rollNo}`.toLowerCase().includes(q));
  }, [students, qText]);

  const exportDailyCSV = () => {
    if (!filteredStudents.length) return;
    const rows = filteredStudents.map(s => {
      const rec = recOf(s) || {};
      return {
        Name: s.name,
        RollNo: s.rollNo || "",
        Branch: s.branch || "",
        Status: STATUS_MAP[(rec?.status || "").toLowerCase()]?.label || "",
        Note: rec.note || "",
        UpdatedAt: rec.updatedAt ? dayjs(rec.updatedAt).format("YYYY-MM-DD HH:mm") : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyAttendance");
    XLSX.writeFile(wb, `attendance_${clean(cls)}_${clean(sec)}_${selectedDate}.xlsx`);
  };

/* ---- attendance (monthly) ---- */
useEffect(() => {
  if (!school?.id || !cls || !sec || mode !== "monthly") return;
  let isStale = false;

  const mStart = dayjs(`${selectedMonth}-01`).startOf("month").toDate();
  const mEnd   = dayjs(`${selectedMonth}-01`).startOf("month").add(1, "month").toDate();

  const col = collection(db, "schools", school.id, "attendance");
  // Include docs written with an empty section ("") as well as the selected section.
  const qRef = query(
    col,
    where("className", "==", cls),
    where("section", "in", [sec, ""]),
    where("date", ">=", mStart),
    where("date", "<", mEnd)
  );

  const unsub = onSnapshot(
    qRef,
    (snap) => {
      if (isStale) return;
      const list = snap.docs.map((d) => {
        const x = d.data() || {};
        const normStatus = normalizeStatusInput(x.status || "");
        return {
          id: d.id,
          ...x,
          status: normStatus,
          // normalize dates so downstream code can rely on JS Date
          date: toDate(x.date),
          createdAt: toDate(x.createdAt),
          updatedAt: toDate(x.updatedAt),
          // prefer stable key; fall back to studentId
          studentKey: String(x.studentKey ?? x.studentId ?? ""),
        };
      });
      setAttMonthly(list);
    },
    () => { /* swallow listener errors to avoid UI crash */ }
  );

  return () => { isStale = true; try { unsub(); } catch {} };
}, [school?.id, cls, sec, selectedMonth, mode]);


  const monthlyAgg = useMemo(() => {
    if (mode !== "monthly") return { rows: [], days: [] };

    const monthStart   = dayjs(selectedMonth + "-01");
    const daysInMonth  = monthStart.daysInMonth();
    const dayKeys      = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const codeByStatus = { present: "P", absent: "A", late: "L", excused: "E", halfday: "H" };

    // Pre-seed rows in stable order with empty days
    const byStudent = new Map();
    students.forEach((s) => {
      const key = studentKeyOf(s);
      byStudent.set(key, {
        student: s,
        days: Object.fromEntries(dayKeys.map((d) => [d, ""])),
        _latest: {}, // { [dayNum]: { code, ts } }
        totals: { P: 0, A: 0, L: 0, E: 0, H: 0, MARKED: 0 },
        percent: 0,
      });
    });

    // Fold raw monthly docs → latest mark per (student, day)
    attMonthly.forEach((rec) => {
      const key = String(rec.studentKey ?? rec.studentId ?? "");
      if (!key) return;
      const row = byStudent.get(key);
      if (!row) return;

      const dObj = toDate(rec.date);
      if (!dObj) return;
      const d = dayjs(dObj);
      if (!d.isValid()) return;

      const dayNum = d.date(); // 1..31
      if (dayNum < 1 || dayNum > daysInMonth) return;

      const code = codeByStatus[normalizeStatusInput(rec.status || "")] || "";
      const ts =
        +toDate(rec.updatedAt) ||
        +toDate(rec.createdAt) ||
        +dObj ||
        0;

      const prev = row._latest[dayNum];
      if (!prev || ts > prev.ts) {
        row._latest[dayNum] = { code, ts };
        row.days[dayNum] = code;
      }
    });

    // Totals + percentage (only over MARKED days)
    byStudent.forEach((row) => {
      dayKeys.forEach((d) => {
        const c = row.days[d];
        if (c) {
          row.totals[c] = (row.totals[c] || 0) + 1;
          row.totals.MARKED += 1;
        }
      });
      const attended = row.totals.P + row.totals.H; // half-day counts as attended
      row.percent = row.totals.MARKED
        ? Math.round((attended / row.totals.MARKED) * 100)
        : 0;
    });

    // Keep order matched to the filtered student list
    const rows = students
      .filter((s) => filteredStudents.some((fs) => fs.id === s.id))
      .map((s) => byStudent.get(studentKeyOf(s)))
      .filter(Boolean);

    return { rows, days: dayKeys };
  }, [mode, selectedMonth, students, filteredStudents, attMonthly]);

  const exportMonthlyCSV = () => {
    if (!monthlyAgg.rows.length) return;
    const rows = monthlyAgg.rows.map(r => ({
      Name: r.student.name,
      RollNo: r.student.rollNo || "",
      Branch: r.student.branch || "",
      ...monthlyAgg.days.reduce((acc, d) => {
        acc[`Day ${d}`] = r.days[d] || "";
        return acc;
      }, {}),
      P: r.totals.P,
      A: r.totals.A,
      L: r.totals.L,
      E: r.totals.E,
      H: r.totals.H,
      Percent: r.percent,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonthlyAttendance");
    XLSX.writeFile(wb, `attendance_${clean(cls)}_${clean(sec)}_${selectedMonth}.xlsx`);
  };

  /* ---- actions / permissions ---- */
  const canEdit = useMemo(() => {
    const r = normalizeRole(role);
    return r === "schooladmin" || r === "teacher" || r === "superadmin";
  }, [role]);

  const currentDateObj = useMemo(() => dayjs(selectedDate).toDate(), [selectedDate]);

  // mirror attendance status to school's student doc
  const mirrorToStudent = async (studentId, status) => {
    if (!school?.id || !studentId) return;
    await setDoc(
      doc(db, "schools", school.id, "students", studentId),
      { lastAttendanceStatus: status || "", lastAttendanceDate: currentDateObj },
      { merge: true }
    );
  };

  // wait for server echo of updated status (per record)
  const waitForAttendanceEcho = (studentId, expectedStatus, timeoutMs = 5000) =>
    new Promise((resolve) => {
      if (!school?.id) return resolve(false);
      const attId = makeAttId(currentDateObj, cls, sec, studentId);
      const ref = doc(db, "schools", school.id, "attendance", attId);
      let done = false;
      const off = onSnapshot(
        ref,
        (snap) => {
          const d = snap.data();
          if (d?.status === expectedStatus) {
            done = true;
            off();
            resolve(true);
          }
        },
        () => { if (!done) { done = true; resolve(false); } }
      );
      setTimeout(() => { if (!done) { done = true; off(); resolve(false); } }, timeoutMs);
    });

  // write one record
  const writeOne = async ({ student, status, note }) => {
    if (!school?.id) return;
    const id = makeAttId(currentDateObj, cls, sec, student.id);
    const normalized = normalizeStatusInput(status);

    // optimistic UI -> write under both keys
    setAttDaily(prev => {
      const next = new Map(prev);
      const base = {
        id,
        studentId: student.id,
        studentKey: studentKeyOf(student),
        status: normalized,
        note: note ?? (recOf(student)?.note ?? ""),
        updatedAt: new Date(),
        markedByName: recOf(student)?.markedByName || "",
        _ts: Date.now(),
      };
      next.set(student.id, base);
      next.set(studentKeyOf(student), base);
      return next;
    });

    const payload = {
      schoolId: school.id,
      date: currentDateObj,
      className: cls,
      section: sec,
      branch: student.branch || "",
      studentId: student.id,
      studentKey: studentKeyOf(student),
      rollNo: String(student.rollNo || ""),
      status: normalized,
      note: note ?? (recOf(student)?.note || ""),
      markedBy: user?.uid || "",
      markedByName: "",
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    await setDoc(doc(db, "schools", school.id, "attendance", id), payload, { merge: true });
    await mirrorToStudent(student.id, normalized);
  };

  // drafts & selection
  const getEffectiveStatus = (student) =>
    draftStatus.has(student.id) ? draftStatus.get(student.id) : (recOf(student)?.status || "");

  const hasPending = (student) =>
    draftStatus.has(student.id) && draftStatus.get(student.id) !== (recOf(student)?.status || "");

  const setDraft = (id, value) => setDraftStatus(prev => { const n = new Map(prev); n.set(id, value); return n; });
  const clearDraft = (ids) => setDraftStatus(prev => {
    const n = new Map(prev);
    (ids || Array.from(n.keys())).forEach(id => n.delete(id));
    return n;
  });

  const selectedCount = selectedIds.size;
  const filteredIds = useMemo(() => filteredStudents.map(s => s.id), [filteredStudents]);
  const allVisibleSelected = selectedCount > 0 && filteredStudents.every(s => selectedIds.has(s.id));
  useEffect(() => {
    if (masterChkRef.current) {
      masterChkRef.current.indeterminate = selectedCount > 0 && !allVisibleSelected;
    }
  }, [selectedCount, allVisibleSelected]);

  const toggleSelectOne = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAllVisible = () => setSelectedIds(prev => {
    if (allVisibleSelected) { const n = new Set(prev); filteredStudents.forEach(s => n.delete(s.id)); return n; }
    const n = new Set(prev); filteredStudents.forEach(s => n.add(s.id)); return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetStatus = async (status) => {
    if (!selectedCount) return;
    const normalized = normalizeStatusInput(status);
    setDraftStatus(prev => {
      const n = new Map(prev);
      students.filter(s => selectedIds.has(s.id)).forEach(stu => n.set(stu.id, normalized));
      return n;
    });
  };

  const invertSelectedDraft = async () => {
    if (!selectedCount) return;
    setDraftStatus(prev => {
      const n = new Map(prev);
      students.filter(s => selectedIds.has(s.id)).forEach(stu => {
        const current = n.has(stu.id) ? n.get(stu.id) : (recOf(stu)?.status || "");
        const next = current === "present" ? "absent" : current === "absent" ? "present" : current;
        n.set(stu.id, next);
      });
      return n;
    });
  };

  const copyYesterdaySelectedDraft = async () => {
    if (!selectedCount) return;
    const yStart = dayjs(selectedDate).subtract(1, "day").startOf("day").toDate();
    const yEnd = dayjs(selectedDate).subtract(1, "day").endOf("day").toDate();
    const col = collection(db, "schools", school.id, "attendance");
    const qRef = query(
      col,
      where("className", "==", cls),
      where("section", "==", sec),
      where("date", ">=", yStart),
      where("date", "<=", yEnd)
    );
    const snap = await getDocs(qRef);
    // index yesterday by both studentId and studentKey
    const byStudent = new Map();
    snap.forEach(d => {
      const x = d.data() || {};
      const normalized = normalizeStatusInput(x?.status || "present");
      if (x.studentId) byStudent.set(String(x.studentId), normalized);
      if (x.studentKey) byStudent.set(String(x.studentKey), normalized);
    });
    if (!byStudent.size) {
      return Swal.fire({ icon: "info", title: "No data from yesterday to copy" });
    }
    setDraftStatus(prev => {
      const n = new Map(prev);
      students.filter(s => selectedIds.has(s.id)).forEach(stu => {
        const val = byStudent.get(stu.id) || byStudent.get(studentKeyOf(stu));
        if (val) n.set(stu.id, val);
      });
      return n;
    });
  };

  const updateSelected = async () => {
    const idsToSave = Array.from(selectedIds).filter(id => draftStatus.has(id));
    if (!idsToSave.length) return;
    try {
      setSavingBulk(true);
      Swal.fire({ title: "Updating selected…", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const ops = idsToSave.map(async (id) => {
        const student = students.find(s => s.id === id);
        const status = normalizeStatusInput(draftStatus.get(id));
        if (!student || status === undefined) return false;
        await writeOne({ student, status });
        const echoed = await waitForAttendanceEcho(student.id, status);
        return echoed;
      });

      const results = await Promise.allSettled(ops);
      const success = results.filter(r => r.status === "fulfilled" && r.value === true).length;
      const fail = results.length - success;

      clearDraft(idsToSave);
      Swal.close();

      await Swal.fire({
        icon: fail ? "warning" : "success",
        title: fail ? "Partially synced" : "Updated",
        text: fail
          ? `${success} synced, ${fail} pending/failed. View may update as Firestore syncs.`
          : `${success} student${success > 1 ? "s" : ""} updated & synced.`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Bulk update failed", text: err?.message || "Unexpected error" });
    } finally {
      setSavingBulk(false);
    }
  };

  const handleNoteBlur = async (studentId, value) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;
    const id = makeAttId(currentDateObj, cls, sec, student.id);
    await setDoc(doc(db, "schools", school.id, "attendance", id), {
      note: value,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  /* ---- derived ---- */
  const dailyTotals = useMemo(() => {
    const totals = { present: 0, absent: 0, late: 0, excused: 0, halfday: 0, unmarked: 0 };
    filteredStudents.forEach(s => {
      const st = recOf(s)?.status || "";
      if (!st) totals.unmarked += 1; else totals[st] = (totals[st] || 0) + 1;
    });
    return totals;
  }, [filteredStudents, recOf]);

  /* ---- UI ---- */
  if (loadingSchool || role == null) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  const StatusSelect = ({ value, onChange, disabled }) => (
    <Form.Select size="sm" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {STATUSES.map(s => (
        <option key={s.key} value={s.key}>{s.label}</option>
      ))}
    </Form.Select>
  );

  const StatusBadge = ({ code }) => {
    const map = { "": "secondary", P: "success", A: "danger", L: "warning", E: "info", H: "secondary" };
    const text = code || "—";
    return <Badge bg={map[code] || "secondary"}>{text}</Badge>;
  };

  return (
    <Container fluid className="p-0 position-relative">
      {(overlay || savingBulk) && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style={{ background: "rgba(255,255,255,.5)", zIndex: 5, backdropFilter: "blur(2px)" }}>
          <Spinner animation="border" size="sm" className="me-2" />
          {savingBulk ? "Updating selected…" : "Loading students…"}
        </div>
      )}

      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Attendance</h5>
          <small className="text-muted">
            {school.name} • {filteredStudents.length}/{students.length} students
          </small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant={mode === "daily" ? "primary" : "outline-primary"} onClick={() => setMode("daily")}>Daily</Button>
            <Button variant={mode === "monthly" ? "primary" : "outline-primary"} onClick={() => setMode("monthly")}>Monthly</Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="p-3 mb-3 shadow-sm rounded-4">
        <Row className="g-2 align-items-end">
          {mode === "daily" ? (
            <Col md={3} sm={6} xs={12}>
              <Form.Label className="mb-1">Date</Form.Label>
              <Form.Control type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </Col>
          ) : (
            <Col md={3} sm={6} xs={12}>
              <Form.Label className="mb-1">Month</Form.Label>
              <Form.Control type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            </Col>
          )}

          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Class</Form.Label>
            <Form.Select value={cls} onChange={(e) => { setCls(e.target.value); setSec(""); }}>
              {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Col>

          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Section</Form.Label>
            <Form.Select value={sec} onChange={(e) => setSec(e.target.value)}>
              {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          </Col>

          <Col md={3} sm={6} xs={12}>
            <Form.Label className="mb-1">Branch</Form.Label>
            <Form.Select value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">All</option>
              {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </Form.Select>
          </Col>

          <Col md={6} sm={12} xs={12}>
            <Form.Label className="mb-1">Search student</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Name or Roll No."
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        {mode === "daily" && (
          <>
            <Row className="mt-3 g-2 align-items-center">
              <Col className="d-flex flex-wrap gap-2">
                <Badge bg="success">Present: {dailyTotals.present}</Badge>
                <Badge bg="danger">Absent: {dailyTotals.absent}</Badge>
                <Badge bg="warning" text="dark">Late: {dailyTotals.late}</Badge>
                <Badge bg="info" text="dark">Excused: {dailyTotals.excused}</Badge>
                <Badge bg="secondary">Half-day: {dailyTotals.halfday}</Badge>
                <Badge bg="light" text="dark">Unmarked: {dailyTotals.unmarked}</Badge>
              </Col>
              <Col className="text-end">
                <ButtonGroup>
                  <Button variant="outline-secondary" size="sm" onClick={exportDailyCSV}><i className="bi bi-download me-1"/>Export CSV</Button>
                </ButtonGroup>
              </Col>
            </Row>

            {/* Bulk actions (selected) */}
            <Row className="mt-2 g-2 align-items-center">
              <Col className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg="dark">Selected: {selectedCount}</Badge>
                <Button size="sm" variant="outline-success" onClick={() => bulkSetStatus("present")} disabled={!canEdit}><i className="bi bi-check2-circle me-1"/>Present</Button>
                <Button size="sm" variant="outline-danger" onClick={() => bulkSetStatus("absent")} disabled={!canEdit}><i className="bi bi-x-circle me-1"/>Absent</Button>
                <Button size="sm" variant="outline-warning" onClick={() => bulkSetStatus("late")} disabled={!canEdit}><i className="bi bi-clock me-1"/>Late</Button>
                <Button size="sm" variant="outline-info" onClick={() => bulkSetStatus("excused")} disabled={!canEdit}><i className="bi bi-info-circle me-1"/>Excused/Leave</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => bulkSetStatus("halfday")} disabled={!canEdit}><i className="bi bi-calendar2-day me-1"/>Half-day</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => bulkSetStatus("")} disabled={!canEdit}><i className="bi bi-eraser me-1"/>Clear</Button>
                <Button size="sm" variant="outline-secondary" onClick={invertSelectedDraft} disabled={!canEdit}><i className="bi bi-arrow-left-right me-1"/>Invert (Draft)</Button>
                <Button size="sm" variant="outline-primary" onClick={copyYesterdaySelectedDraft} disabled={!canEdit}><i className="bi bi-copy me-1"/>Copy Yesterday (Draft)</Button>
                <Button size="sm" variant="primary" onClick={updateSelected} disabled={!canEdit || !Array.from(selectedIds).some(id => draftStatus.has(id))}>
                  {savingBulk ? (<><Spinner animation="border" size="sm" className="me-1" /> Updating…</>) : (<><i className="bi bi-upload me-1" /> Update Selected</>)}
                </Button>
                <Button size="sm" variant="outline-dark" onClick={clearSelection}><i className="bi bi-x-lg me-1"/>Clear Selection</Button>
              </Col>
            </Row>

            <div className="small text-muted mt-2">
              Legend: <Badge bg="success">P</Badge> Present · <Badge bg="danger">A</Badge> Absent · <Badge bg="warning" text="dark">L</Badge> Late · <Badge bg="info" text="dark">E</Badge> Excused/Leave · <Badge bg="secondary">H</Badge> Half-day
            </div>
          </>
        )}

        {mode === "monthly" && (
          <Row className="mt-3 g-2 align-items-center">
            <Col className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={exportMonthlyCSV}><i className="bi bi-download me-1"/>Export CSV</Button>
            </Col>
          </Row>
        )}
      </Card>

      {/* Daily table — themed like Teacher */}
      {mode === "daily" && (
        <Card className="p-3 shadow-sm rounded-4">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="sticky-top bg-body">
                <tr>
                  <th style={{width: 36}}>
                    <Form.Check
                      ref={masterChkRef}
                      type="checkbox"
                      checked={filteredStudents.length > 0 && allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible students"
                    />
                  </th>
                  <th style={{minWidth: 220}}>Student</th>
                  <th className="d-none d-md-table-cell">Roll No.</th>
                  <th className="d-none d-lg-table-cell">Branch</th>
                  <th>Status (draft)</th>
                  <th className="d-none d-lg-table-cell" style={{width: 280}}>Note</th>
                  <th className="d-none d-xl-table-cell">Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-5"><Spinner animation="border" size="sm"/> Loading…</td></tr>
                ) : filteredStudents.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted py-4">
                    No students found. Check if <strong>Class</strong>/<strong>Section</strong> match your student records.
                  </td></tr>
                ) : (
                  filteredStudents.map(s => {
                    const rec = recOf(s);
                    const checked = selectedIds.has(s.id);
                    const saving = savingRowIds.has(s.id);
                    return (
                      <tr key={s.id} className={hasPending(s) ? "table-warning" : ""}>
                        <td className="sticky-col col-0">
                          <Form.Check
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelectOne(s.id)}
                            aria-label={`Select ${s.name}`}
                          />
                        </td>
                        <td className="sticky-col col-1">
                          <div className="fw-semibold text-truncate text-muted" style={{maxWidth: 260}}>
                            {s.name}
                            {hasPending(s) && <span className="ms-2 badge bg-warning text-dark">unsaved</span>}
                          </div>
                          <div className="text-muted small">Section: {s.section || "—"}</div>
                          {s.lastAttendanceStatus ? (
                            <div className="small mt-1">
                              <Badge bg="light" text="dark" className="me-1">Last</Badge>
                              <Badge bg={STATUS_MAP[s.lastAttendanceStatus]?.variant || "secondary"}>
                                {STATUS_MAP[s.lastAttendanceStatus]?.code || "—"}
                              </Badge>
                              <span className="text-muted ms-1">
                                {s.lastAttendanceDate ? dayjs(s.lastAttendanceDate).format("DD MMM,hh:mm A") : ""}
                              </span>
                            </div>
                          ) : null}
                        </td>

                        <td className="d-none d-md-table-cell">{s.rollNo || "—"}</td>
                        <td className="d-none d-lg-table-cell text-muted">{s.branch || "—"}</td>

                        <td style={{ width: 320 }}>
                          <div className="d-flex gap-2 align-items-center flex-wrap text-muted">
                            <StatusSelect
                              value={getEffectiveStatus(s)}
                              onChange={(v) => setDraft(s.id, normalizeStatusInput(v))}
                              disabled={!canEdit || saving}
                            />
                            <div className="btn-group btn-group-sm" role="group" aria-label="Quick status">
                              <Button variant="outline-success"  disabled={!canEdit || saving} onClick={() => setDraft(s.id, "present")}>P</Button>
                              <Button variant="outline-danger"   disabled={!canEdit || saving} onClick={() => setDraft(s.id, "absent")}>A</Button>
                              <Button variant="outline-warning"  disabled={!canEdit || saving} onClick={() => setDraft(s.id, "late")}>L</Button>
                              <Button variant="outline-info"     disabled={!canEdit || saving} onClick={() => setDraft(s.id, "excused")}>E</Button>
                              <Button variant="outline-secondary" disabled={!canEdit || saving} onClick={() => setDraft(s.id, "halfday")}>H</Button>
                            </div>
                          </div>
                        </td>

                        <td className="d-none d-lg-table-cell">
                          <Form.Control
                            size="sm"
                            placeholder="Note (optional)"
                            defaultValue={rec?.note || ""}
                            onBlur={(e) => handleNoteBlur(s.id, e.target.value)}
                            disabled={!canEdit || saving}
                          />
                        </td>

                        <td className="d-none d-xl-table-cell text-muted">{fmtTime(rec?.updatedAt)}</td>

                        <td style={{ width: 160 }}>
                          <Button
                            size="sm"
                            variant={hasPending(s) ? "primary" : "outline-secondary"}
                            disabled={!canEdit || !hasPending(s) || saving}
                            onClick={async () => {
                              const student = students.find(x => x.id === s.id);
                              const status  = normalizeStatusInput(draftStatus.get(s.id));
                              if (!student || status === undefined) return;

                              try {
                                setSavingRowIds(prev => new Set(prev).add(s.id));

                                Swal.fire({
                                  title: `Saving ${student.name}…`,
                                  allowOutsideClick: false,
                                  didOpen: () => Swal.showLoading(),
                                });

                                await writeOne({ student, status });

                                const echoed = await waitForAttendanceEcho(student.id, status);

                                Swal.close();
                                clearDraft([s.id]);

                                await Swal.fire({
                                  icon: echoed ? "success" : "info",
                                  title: echoed ? "Updated" : "Updated (sync pending)",
                                  text: echoed
                                    ? `${student.name} marked ${STATUS_MAP[status]?.label || status}`
                                    : `Saved locally. Firestore sync will update the view shortly.`,
                                  timer: 1200,
                                  showConfirmButton: false,
                                });
                              } catch (err) {
                                Swal.close();
                                await Swal.fire({ icon: "error", title: "Update failed", text: err?.message || "Write failed" });
                              } finally {
                                setSavingRowIds(prev => {
                                  const n = new Set(prev);
                                  n.delete(s.id);
                                  return n;
                                });
                              }
                            }}
                          >
                            {saving ? (<><Spinner animation="border" size="sm" className="me-1" /> Saving…</>) : "Update"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* Monthly grid — themed like Teacher (cards + horizontal day strip) */}
      {mode === "monthly" && (
        <Card className="p-3 shadow-sm rounded-4">
          <Row className="g-2 align-items-center mb-2">
            <Col>
              <div className="text-muted small">
                Month: {dayjs(selectedMonth + "-01").format("MMMM YYYY")}
              </div>
            </Col>
            <Col className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={exportMonthlyCSV}>
                <i className="bi bi-download me-1" /> Export CSV
              </Button>
            </Col>
          </Row>

          <div className="month-grid">
            {loading ? (
              <div className="py-5 text-center text-muted">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : monthlyAgg.rows
                .filter((r) => filteredStudents.some((s) => s.id === r.student.id))
                .map((r) => (
                  <Card key={r.student.id} className="month-card shadow-sm">
                    <Card.Body className="p-3">
                      {/* Header */}
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="fw-semibold text-truncate">{r.student.name || "—"}</div>
                          <div className="small text-muted">
                            <span className="me-3">Roll: {r.student.rollNo || "—"}</span>
                            <span>Branch: {r.student.branch || "—"}</span>
                          </div>
                        </div>
                        {/* Totals */}
                        <div className="d-flex flex-wrap gap-1 text-nowrap">
                          <Badge bg="success">P {r.totals.P}</Badge>
                          <Badge bg="danger">A {r.totals.A}</Badge>
                          <Badge bg="warning" text="dark">L {r.totals.L}</Badge>
                          <Badge bg="info" text="dark">E {r.totals.E}</Badge>
                          <Badge bg="secondary">H {r.totals.H}</Badge>
                        </div>
                      </div>

                      {/* Days strip */}
                      <div className="day-strip" role="list" aria-label="Attendance by day">
                        {monthlyAgg.days.map((d) => {
                          const dateObj = dayjs(selectedMonth + `-${String(d).padStart(2, "0")}`);
                          const dayName = dateObj.format("ddd");
                          return (
                            <div role="listitem" key={d} className="day-chip">
                              <div className="day-num">{d}</div>
                              <div className="day-name">{dayName}</div>
                              <div className="day-code">
                                <StatusBadge code={r.days[d]} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
          </div>

          <div className="small text-muted mt-3">
            Legend: <Badge bg="success">P</Badge> Present · <Badge bg="danger">A</Badge> Absent ·
            <Badge bg="warning" text="dark" className="ms-1">L</Badge> Late ·
            <Badge bg="info" text="dark" className="ms-1">E</Badge> Excused/Leave ·
            <Badge bg="secondary" className="ms-1">H</Badge> Half-day
          </div>
        </Card>
      )}

      {/* Theme polish (match Teacher) */}
      <style>{`
        /* Modern look */
        .card { border-radius: 16px; }
        .form-control, .form-select { border-radius: 12px; }
        .btn { border-radius: 10px; }
        .table > :not(caption) > * > * { vertical-align: middle; }

        /* Unsaved tint */
        tr.table-warning { --bs-table-bg: color-mix(in oklab, var(--bs-warning-bg, #fff3cd) 45%, transparent) !important; }

        /* Monthly cards layout */
        .month-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 14px;
        }
        .month-card { border-radius: 14px; }

        /* Horizontal, scrollable strip of day chips */
        .day-strip {
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: 52px; /* width per day cell */
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 6px;
          scrollbar-gutter: stable both-edges;
        }

        /* Each day cell (number, name, status) */
        .day-chip {
          display: grid;
          grid-template-rows: auto auto auto;
          align-items: center;
          justify-items: center;
          border: 1px solid var(--bs-border-color);
          border-radius: 10px;
          padding: 4px 2px;
          min-height: 72px;
          background: color-mix(in oklab, var(--bs-body-bg, #fff) 98%, transparent);
        }
        .day-num {
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
          margin-bottom: 2px;
        }
        .day-name {
          font-size: 11px;
          color: var(--bs-secondary-color);
          margin-bottom: 4px;
        }
        .day-code .badge {
          width: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        /* Nice scrollbars for the strip */
        .day-strip::-webkit-scrollbar { height: 8px; }
        .day-strip::-webkit-scrollbar-thumb {
          background: color-mix(in oklab, var(--bs-secondary-color, #adb5bd) 40%, transparent);
          border-radius: 8px;
        }
        .day-strip::-webkit-scrollbar-track {
          background: color-mix(in oklab, var(--bs-body-bg, #fff) 90%, transparent);
        }

        /* Smaller screens */
        @media (max-width: 480px) {
          .month-grid { grid-template-columns: 1fr; }
          .day-strip { grid-auto-columns: 46px; }
        }
      `}</style>
    </Container>
  );
}
